import { Fragment, useState, useCallback, useEffect, useRef } from 'react'

import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Collapse from '@mui/material/Collapse'
import Box from '@mui/material/Box'
import Badge from '@mui/material/Badge'

import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { Link, useLocation } from 'react-router-dom'
import { IRoutes } from '../../../../../routes/route.interface'
import { ERouteKey } from '../../../../../compiler/route.enum'
import { useLineChatAlertContext } from '../../../../../contexts/LineChatAlertContext'
import RequestPendingBadge from './RequestPendingBadge'

// Shared link styles — remove default <a> styling
const linkResetSx = {
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
} as const;

export default function MenuItem({ name, path = '', icon, children, ...rest }: IRoutes) {

    const location = useLocation();
    const { unreadConversationCount, staleUnreadCount, hasLineChatPermission } = useLineChatAlertContext();

    const normalizePath = (value: string): string => {
        if (!value) return '/';
        const withLeadingSlash = value.startsWith('/') ? value : `/${value}`;
        return withLeadingSlash.replace(/\/+$/, '') || '/';
    };

    const isPathActive = (targetPath: string): boolean => {
        const target = normalizePath(targetPath);
        const current = normalizePath(location.pathname);
        return current === target || current.startsWith(`${target}/`);
    };

    const isActive = isPathActive(path);

    const [isOpen, setIsOpen] = useState<boolean>(isActive)
    const isInitialMount = useRef(true);

    // Auto-expand when active, auto-collapse when not active
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        setIsOpen(isActive);
    }, [isActive]);

    const toggleExpand = useCallback(() => {
        if (children) {
            setIsOpen(prev => !prev);
        }
    }, [children]);

    const isActiveLink = (itemPath: string): boolean => {
        return isPathActive(`${path}/${itemPath}`)
    }

    const HAS_SUB_LINK: boolean = Boolean(!children?.every(i => i.isPrivateLink) && children)

    // Parent item: has sub-links → expand on click; no sub-links → navigate via <Link>
    const parentContent = (
        <ListItemButton sx={{
            border: '1px solid transparent',
            ...isActive && {
                bgcolor: HAS_SUB_LINK ? 'unset' : 'primary.main',
                color: HAS_SUB_LINK ? 'primary.dark' : 'white',
                '& .MuiSvgIcon-root': {
                    color: HAS_SUB_LINK ? 'primary.dark' : 'white',
                }
            },
            ':hover': {
                bgcolor: 'primary.light',
                borderRadius: 2,
                border: theme => `1px solid ${theme.palette.primary.main}`,
                color: 'primary.dark',
                '& .MuiSvgIcon-root': {
                    color: 'primary.dark',
                }
            },
            mx: 1,
            mt: 1,
            mb: 0,
            p: 0,
            borderRadius: 2,
            transition: theme => theme.transitions.create('background-color', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            })
        }}
            {...(HAS_SUB_LINK ? { onClick: toggleExpand } : {})}
        >
            {icon && (
                <ListItemIcon sx={{ minWidth: '48px', m: 0, p: 1.5 }}>
                    {icon}
                </ListItemIcon>
            )}
            <ListItemText sx={{ m: 0, p: 0 }} primary={name} />
            {normalizePath(path) === '/request-center' && <RequestPendingBadge />}
            {
                HAS_SUB_LINK && (
                    <Box pr={1} onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleExpand(); }}>
                        {isOpen ? <ExpandLess /> : <ExpandMore />}
                    </Box>)
            }
        </ListItemButton>
    );

    return (
        <Fragment>
            {HAS_SUB_LINK ? (
                // Parent with children: just expand/collapse, no link
                parentContent
            ) : (
                // Leaf parent: wrap in <Link> for native anchor behavior
                <Link to={'/' + path} style={linkResetSx}>
                    {parentContent}
                </Link>
            )}
            {!children?.every(i => i.isPrivateLink) && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List component="div" sx={{ pl: children ? 1 : 0 }}>
                        {children?.filter(item => item.path && !item.isPrivateLink && item.key !== ERouteKey.WELCOME && item.path !== 'welcome')?.map((item, index: number) => {
                            const isLineChatHistory = path === 'customer' && item.path === 'line-chat-history';
                            const hasUnreadBadge = isLineChatHistory && hasLineChatPermission && unreadConversationCount > 0;

                            return (
                                <Fragment key={index}>
                                    <Link to={'/' + path + '/' + (item.path || '')} style={linkResetSx}>
                                        <ListItemButton sx={{
                                            border: '1px solid transparent',
                                            ...isActiveLink(item.path) && {
                                                bgcolor: 'primary.light',
                                            },
                                            ':hover': {
                                                bgcolor: 'primary.light',
                                            },
                                            mb: 0.5,
                                            pl: 4,
                                            p: 0,
                                        }}>
                                            <ListItemText
                                                color='text.secondary'
                                                sx={{ m: 0, py: 1, pl: 6 }}
                                                primary={
                                                    hasUnreadBadge ? (
                                                        <Badge
                                                            color={staleUnreadCount > 0 ? 'error' : 'warning'}
                                                            badgeContent={unreadConversationCount > 99 ? '99+' : unreadConversationCount}
                                                            max={999}
                                                        >
                                                            <Box component="span" pr={2}>
                                                                {item.name}
                                                            </Box>
                                                        </Badge>
                                                    ) : (item.sidebarName ?? item.name)
                                                }
                                            />
                                        </ListItemButton>
                                    </Link>
                                </Fragment>
                            );
                        })}
                    </List>
                </Collapse>
            )}
        </Fragment>
    );
}
